import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "lib/utils";
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  ShoppingCart,
  XCircle,
} from "lucide-react";

import { queryKeys } from "hooks/queryKeys";
import { useCancelQuotaOrder, useGetQuotaOrder } from "hooks/useQuotas";

import PaynetReceipt from "js/components/Payments/PaynetReceipt";
import SpeiPaymentSlip from "js/components/Payments/SpeiPaymentSlip";
import {
  ORDER_STATUS_COLORS,
  ORDER_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  QUOTE_TYPE_LABELS,
  formatCurrency,
} from "js/components/Quotas/QuotaConstants";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "js/components/ui/alert-dialog";
import { Button } from "js/components/ui/button";

import type { PaymentData } from "js/pages/Payments/types";
import type { OrderStatus, QuotaOrder } from "./types";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const formatShortDate = (dateStr: string | null) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString("es-MX", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function getRelativeTime(dateStr: string): string | null {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "ahora";
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours}h`;
  return null;
}

/* ------------------------------------------------------------------ */
/*  Timeline                                                          */
/* ------------------------------------------------------------------ */

type StepStatus = "complete" | "in-progress" | "pending" | "failed";

interface TimelineStep {
  label: string;
  status: StepStatus;
  date: string | null;
}

const TERMINAL_STATUSES: OrderStatus[] = ["COMPLETED", "FAILED", "CANCELLED"];

const getTimelineSteps = (order: QuotaOrder): TimelineStep[] => {
  const { status } = order;

  if (status === "FAILED") {
    return [
      { label: "Pedido creado", status: "complete", date: order.created_at },
      { label: "Pago fallido", status: "failed", date: order.completed_at },
    ];
  }

  if (status === "CANCELLED") {
    return [
      { label: "Pedido creado", status: "complete", date: order.created_at },
      { label: "Orden cancelada", status: "failed", date: order.completed_at },
    ];
  }

  const done = status === "COMPLETED";
  const processing = status === "PAYMENT_IN_PROGRESS";

  return [
    { label: "Pedido creado", status: "complete", date: order.created_at },
    {
      label: "Pago recibido",
      status: done ? "complete" : processing ? "in-progress" : "pending",
      date: done ? order.completed_at : null,
    },
    {
      label: "Cr\u00e9ditos agregados",
      status: done ? "complete" : "pending",
      date: done ? order.completed_at : null,
    },
  ];
};

/* ------------------------------------------------------------------ */
/*  Horizontal timeline style maps                                    */
/* ------------------------------------------------------------------ */

const circleClasses: Record<StepStatus, string> = {
  complete: "bg-emerald-500 text-white",
  "in-progress": "bg-blue-500 text-white ring-4 ring-blue-500/20",
  pending: "border-2 border-muted-foreground/20 bg-card",
  failed: "bg-red-500 text-white",
};

const labelClasses: Record<StepStatus, string> = {
  complete: "text-emerald-700 dark:text-emerald-400 font-medium",
  "in-progress": "text-blue-700 dark:text-blue-400 font-medium",
  pending: "text-muted-foreground",
  failed: "text-red-700 dark:text-red-400 font-medium",
};

const lineColor = (nextStatus: StepStatus) => {
  if (nextStatus === "complete") return "bg-emerald-500";
  if (nextStatus === "in-progress")
    return "bg-gradient-to-r from-emerald-500 to-blue-500";
  if (nextStatus === "failed") return "bg-red-300";
  return "bg-muted-foreground/20";
};

/* ------------------------------------------------------------------ */
/*  Compact metadata date formatter                                   */
/* ------------------------------------------------------------------ */

const formatMetaDate = (dateStr: string) =>
  new Date(dateStr).toLocaleString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

const MAX_POLLS = 12;

const QuotasOrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const cancelMutation = useCancelQuotaOrder();

  const [searchParams] = useSearchParams();
  const redirectStatus = searchParams.get("status");

  const pollCountRef = useRef(0);
  const [shouldPoll, setShouldPoll] = useState(true);
  const [pollingStopped, setPollingStopped] = useState(false);

  const {
    data: order,
    isLoading,
    isError,
    refetch,
  } = useGetQuotaOrder(orderId ?? "", true, shouldPoll ? 5000 : false) as {
    data: QuotaOrder | undefined;
    isLoading: boolean;
    isError: boolean;
    refetch: () => void;
  };

  useEffect(() => {
    if (!order) return;
    if (TERMINAL_STATUSES.includes(order.status)) {
      setShouldPoll(false);
      setPollingStopped(false);
      if (order.status === "COMPLETED") {
        queryClient.invalidateQueries({
          queryKey: queryKeys.organization.credits(),
        });
      }
    } else {
      pollCountRef.current += 1;
      if (pollCountRef.current >= MAX_POLLS) {
        setShouldPoll(false);
        setPollingStopped(true);
      }
    }
  }, [order, queryClient]);

  const handleCancel = () => {
    if (orderId) {
      cancelMutation.mutate(orderId, {
        onSuccess: () => navigate("/organizations/quotas"),
      });
    }
  };

  const handleManualRefresh = () => {
    pollCountRef.current = 0;
    setPollingStopped(false);
    setShouldPoll(true);
    refetch();
  };

  const backPath = "/organizations/quotas";

  /* ---- Loading --------------------------------------------------- */
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  /* ---- Error ----------------------------------------------------- */
  if (isError || !order) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <button
          type="button"
          onClick={() => navigate(backPath)}
          className="group inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          <span>Volver</span>
        </button>
        <div className="rounded-2xl border border-border/60 bg-card p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
            <XCircle className="h-7 w-7 text-destructive" />
          </div>
          <p className="text-sm text-muted-foreground">
            Error al cargar el detalle de la orden.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(backPath)}
          >
            Volver a cuotas
          </Button>
        </div>
      </div>
    );
  }

  /* ---- Derived state --------------------------------------------- */
  const isCompleted = order.status === "COMPLETED";
  const isPending =
    order.status === "PENDING" || order.status === "PAYMENT_IN_PROGRESS";
  const canCancel = order.status === "PENDING" || order.status === "PAYMENT_IN_PROGRESS";
  const timelineSteps = getTimelineSteps(order);
  const paymentMethodLabel =
    PAYMENT_METHOD_LABELS[order.payment_method] ?? order.payment_method;

  /* ---- Shared timeline renderer ---------------------------------- */
  const renderTimeline = () => (
    <div className="flex items-start">
      {timelineSteps.map((step, i) => (
        <React.Fragment key={step.label}>
          {/* Step */}
          <div className="flex flex-col items-center" style={{ minWidth: 72 }}>
            <div
              className={cn(
                "flex size-7 shrink-0 items-center justify-center rounded-full transition-all",
                circleClasses[step.status],
              )}
            >
              {step.status === "complete" && (
                <Check className="size-3.5" />
              )}
              {step.status === "in-progress" && (
                <Clock className="size-3.5" />
              )}
              {step.status === "failed" && (
                <XCircle className="size-3.5" />
              )}
            </div>
            <span
              className={cn(
                "mt-1.5 text-xs text-center leading-tight",
                labelClasses[step.status],
              )}
            >
              {step.label}
            </span>
            {step.date && (
              <span className="mt-0.5 text-[10px] text-muted-foreground text-center">
                {formatShortDate(step.date)}
                {getRelativeTime(step.date) && (
                  <span className="ml-0.5 text-muted-foreground/60">
                    ({getRelativeTime(step.date)})
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Connecting line */}
          {i < timelineSteps.length - 1 && (
            <div className="flex flex-1 items-center pt-3.5">
              <div
                className={cn(
                  "h-px w-full",
                  lineColor(timelineSteps[i + 1].status),
                )}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  /* ---- Shared polling indicator renderer ------------------------- */
  const renderPollingIndicator = () => {
    if (TERMINAL_STATUSES.includes(order.status)) return null;

    if (pollingStopped) {
      return (
        <div className="border-t border-border/60 bg-amber-50/50 dark:bg-amber-950/20 px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                La verificación automática se detuvo
              </span>
              <span className="text-xs text-muted-foreground">
                Puedes verificar manualmente el estado del pago
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualRefresh}
              className="shrink-0 gap-1.5"
            >
              <RefreshCw className="size-3.5" />
              Verificar ahora
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="border-t border-border/60 bg-blue-50/50 dark:bg-blue-950/20 px-6 py-3">
        <div className="flex items-center gap-2.5">
          <Loader2 className="size-4 shrink-0 animate-spin text-blue-500" />
          <div className="flex flex-col gap-0.5">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                Verificando pago...
              </span>
              <span className="text-xs text-muted-foreground">
                Se actualiza automáticamente
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              Puedes salir de esta página. El estado se actualizará automáticamente.
            </span>
          </div>
        </div>
      </div>
    );
  };

  /* ---- Shared cancel action renderer ----------------------------- */
  const renderCancelAction = () => {
    if (!canCancel) return null;

    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button
            type="button"
            disabled={cancelMutation.isPending}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-destructive hover:text-destructive/80 disabled:opacity-50"
          >
            {cancelMutation.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Cancelando...
              </>
            ) : (
              "Cancelar orden"
            )}
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Cancelar esta orden?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, mantener</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sí, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  /* ---- Render: Pending states (action card first) ---------------- */
  if (isPending) {
    return (
      <div className="mx-auto max-w-2xl space-y-5">
        {/* Back button */}
        <button
          type="button"
          onClick={() => navigate(backPath)}
          className="group inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          <span>Volver</span>
        </button>

        {/* 3D Secure redirect banner */}
        {redirectStatus === "success" && order.status === "PAYMENT_IN_PROGRESS" && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-blue-800 font-medium">Procesando tu pago...</p>
            <p className="text-blue-600 text-sm mt-1">Tu pago está siendo verificado. Esta página se actualizará automáticamente.</p>
          </div>
        )}

        {/* Status card FIRST — visible on all screen sizes */}
        <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-4 min-w-0 flex-wrap">
                <span className="text-lg font-bold tabular-nums text-foreground">
                  {formatCurrency(order.total_amount)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {order.quantity.toLocaleString("es-MX")} consultas{" "}
                  {QUOTE_TYPE_LABELS[order.quote_type]}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatMetaDate(order.created_at)} &middot; {paymentMethodLabel}
                </span>
              </div>
              <span
                className={cn(
                  "inline-flex shrink-0 items-center rounded-full border px-3 py-1 text-xs font-semibold",
                  ORDER_STATUS_COLORS[order.status],
                )}
              >
                {ORDER_STATUS_LABELS[order.status]}
              </span>
            </div>
          </div>

          <div className="border-t border-border/60 px-6 py-4">
            {renderTimeline()}
          </div>

          {renderPollingIndicator()}

          {canCancel && (
            <div className="border-t border-border/60 px-6 py-3 flex justify-end">
              {renderCancelAction()}
            </div>
          )}
        </div>

        {/* Payment instructions card SECOND */}
        {order.payment_data && Object.keys(order.payment_data).length > 0 && order.payment_method === "bank_account" && (
          <SpeiPaymentSlip
            paymentData={order.payment_data as PaymentData}
            amount={order.total_amount}
          />
        )}
        {order.payment_data && Object.keys(order.payment_data).length > 0 && order.payment_method === "store" && (
          <PaynetReceipt
            paymentData={order.payment_data as PaymentData}
            amount={order.total_amount}
          />
        )}
      </div>
    );
  }

  /* ---- Render: Terminal states (order summary as primary) --------- */
  return (
    <div className="mx-auto max-w-2xl space-y-5">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate(backPath)}
        className="group inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
        <span>Volver</span>
      </button>

      {/* Main card (primary for terminal states) */}
      <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
        {/* Header section */}
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-3xl font-bold tabular-nums text-foreground">
                {formatCurrency(order.total_amount)}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {order.quantity.toLocaleString("es-MX")} consultas{" "}
                {QUOTE_TYPE_LABELS[order.quote_type]}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {formatMetaDate(order.created_at)} &middot; {paymentMethodLabel}
              </p>
            </div>
            <span
              className={cn(
                "inline-flex shrink-0 items-center rounded-full border px-3 py-1 text-xs font-semibold",
                ORDER_STATUS_COLORS[order.status],
              )}
            >
              {ORDER_STATUS_LABELS[order.status]}
            </span>
          </div>
        </div>

        {/* Horizontal timeline */}
        <div className="border-t border-border/60 px-6 py-4">
          {renderTimeline()}
        </div>
      </div>

      {/* Success banner (below card) */}
      <AnimatePresence>
        {isCompleted && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className="flex items-center gap-4 rounded-2xl bg-emerald-50 px-6 py-5 dark:bg-emerald-950/30"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 15,
                delay: 0.1,
              }}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/50"
            >
              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </motion.div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-bold text-emerald-900 dark:text-emerald-100">
                Compra completada
              </h2>
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                Se añadieron{" "}
                <span className="font-bold">
                  {order.quantity.toLocaleString("es-MX")} consultas{" "}
                  {QUOTE_TYPE_LABELS[order.quote_type]}
                </span>{" "}
                a tu organización.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => navigate("/organizations/quotas")}
              className="shrink-0 gap-1.5"
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              Comprar de nuevo
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuotasOrderDetail;
